import { FaustTemplate } from '@faustwp/core';
import classNames from 'classnames';

import GetPost from './postQuery.gql';
import { GetPostQuery, PostEditorBlockFragmentFragment } from '../../__generated__/graphql';
import EntryHeader from '../../components/EntryHeader';
import PostAside from '../../components/PostAside';
import { mapAcfBlockToContentBlock } from '../../content-blocks/mapContentBlocks';
import { AcfFragment } from '../../content-blocks/types';
import Layout from '../../layout';
import { getPostFeatureImage } from '../../layout/helpers';
import { sanitizeHtml } from '../../utils/sanitzeHtml';
import { gql } from '../helpers';

import style from './single.module.css';

const Component: FaustTemplate<GetPostQuery> = props => {
  // Loading state for previews
  if (props.loading || !props.data) {
    return <>Loading...</>;
  }

  const {
    footerMenus,
    headerLogo: headerLogoData,
    headerMenuItems,
    menuButton: menuButtonData,
    post,
    systemAlert,
  } = props.data ?? {};
  const {
    author,
    categories,
    date,
    editorBlocks,
    title,
  } = post ?? {};

  const category = categories?.nodes[0];

  const allCategories = (props.data.categories?.nodes ?? [])
    .filter(cat => !!cat.count);

  const relatedPosts = category?.posts?.nodes ?? [];

  const featureImage = getPostFeatureImage(post);

  const blocks = (editorBlocks?.filter(Boolean) ?? []) as PostEditorBlockFragmentFragment[];

  const firstBlock = blocks[0];

  const isVideoArticle = firstBlock?.name === 'core/video' || firstBlock?.name === 'acf/video';

  return (
    <Layout
      containerClassName={classNames('container', style.main, { [style.videoArticle]: isVideoArticle })}
      isLoading={!!props.loading}
      layoutData={{
        footerMenus,
        headerLogo: headerLogoData,
        headerMenuItems,
        menuButton: menuButtonData,
        systemAlert,
      }}
      postData={props.data}
    >
      <EntryHeader
        author={author?.node}
        authorClassName={style.author}
        date={date ? new Date(date) : null}
        featuredImageContainerClassName={style.featuredImage}
        featureImage={featureImage}
        headerMenuItems={headerMenuItems?.nodes ?? null}
        title={title ?? ''}
      />

      <article className={style.blogContent}>
        {blocks.map((fragment, index) => {
          const renderedElement = mapAcfBlockToContentBlock('post', post?.id ?? '')((fragment as AcfFragment), index);

          if (renderedElement) {
            return renderedElement;
          }

          if (fragment.renderedHtml) {
            return (
              <div
                className={style.coreWpBlock}
                // eslint-disable-next-line @typescript-eslint/naming-convention
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(fragment.renderedHtml) }}
                key={`${index.toString()}-${fragment.__typename ?? ''}`}
              />
            );
          }

          return null;
        })}
      </article>

      <PostAside
        categories={allCategories}
        className={style.blogAside}
        currentPostId={post?.id ?? ''}
        postAsideContent={post?.postAsideContent ?? null}
        relatedPosts={relatedPosts}
      />
    </Layout>
  );
};

Component.variables = ({ databaseId }, ctx) => {
  return {
    asPreview: ctx?.asPreview,
    databaseId,
  };
};

Component.query = gql(GetPost);

export default Component;
